import { type Column, type RowData, type Table } from "@tanstack/react-table";
import { Tooltip } from "./Tooltip";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  type DropdownMenuCheckboxItemProps,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ColumnsIcon } from "lucide-react";

export function ColumnVisibilityToggle<TData extends RowData>({ table, disabled }: { table: Table<TData>; disabled: boolean }) {
  const hideableColumns = table.getAllColumns().filter((column) => column.getCanHide());

  const toggleColumns = () => {
    table.getVisibleFlatColumns().length === 1
      ? table.resetColumnVisibility()
      : table.setColumnVisibility({
          ...hideableColumns.reduce((acc, column, index) => {
            return index === 0 ? { [column.id]: true } : { ...acc, [column.id]: false };
          }, {}),
        });
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="ml-auto" disabled={disabled}>
          <ColumnsIcon className="mr-2 h-4 w-4" />
          Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="left">
        <DropdownMenuCheckboxItem onSelect={(event) => event.preventDefault()} onCheckedChange={toggleColumns} className="font-semibold">
          {table.getVisibleFlatColumns().length === 1 ? "Show All" : "Hide"}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />

        {hideableColumns
          .sort((a, b) => {
            const firstHeader = getColumnHeader(a);
            const secondHeader = getColumnHeader(b);
            if (firstHeader < secondHeader) {
              return -1;
            }
            if (firstHeader > secondHeader) {
              return 1;
            }
            return 0;
          })
          .map((column) => {
            const isDisabled = table.getVisibleFlatColumns().length === 1 && column.getIsVisible();

            const checkboxProps: DropdownMenuCheckboxItemProps = {
              className: "capitalize",
              checked: column.getIsVisible(),
              onSelect: (event: Event) => event.preventDefault(),
              onCheckedChange: (value: boolean) => column.toggleVisibility(!!value),
              disabled: isDisabled,
            };

            return isDisabled ? (
              <Tooltip key={column.id} label="At least one column must remain visible" side="left">
                <DropdownMenuCheckboxItem {...checkboxProps}>{getColumnHeader(column)}</DropdownMenuCheckboxItem>
              </Tooltip>
            ) : (
              <DropdownMenuCheckboxItem key={column.id} {...checkboxProps}>
                {getColumnHeader(column)}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function getColumnHeader<TData extends RowData>(column: Column<TData>) {
  if (typeof column.columnDef.header === "string") {
    return column.columnDef.header;
  }
  if (typeof column.columnDef.header === "function") {
    return column.id;
  }
  return column.id;
}
